import { useEffect, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { de, enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Calendar } from '@/components/new-ui/calendar';
import { Input } from '@/components/new-ui/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/new-ui/popover';
import { cn } from '@/lib/utils';

dayjs.extend(customParseFormat);

// Same typed formats as the old date picker, by the UI language
const GERMAN_DATE_PATTERN = /^([012][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/;
const DEFAULT_DATE_PATTERN = /^(0[1-9]|1[012])\.([012][0-9]|3[01])\.\d{4}$/;

/** Display and typing format of the date field, DD.MM.YYYY in German, MM.DD.YYYY otherwise */
export const getDateFieldFormat = (language: string) =>
  language === 'de' ? 'DD.MM.YYYY' : 'MM.DD.YYYY';

type DateFieldProps = {
  id?: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  /** Called with false while the typed text is not a valid date, and with true once it is */
  onValidityChange?: (isValid: boolean) => void;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  invalid?: boolean;
};

/**
 * Date field of the redesign: the date can be typed in the input or picked in the calendar popover.
 * A typed date only reaches `onChange` once it is complete and valid, like the old date picker.
 */
export const DateField = ({
  id,
  value,
  onChange,
  onValidityChange,
  ariaLabel,
  disabled,
  className,
  invalid,
}: DateFieldProps) => {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const dateFormat = getDateFieldFormat(i18n.language);
  const hasValue = value instanceof Date && !Number.isNaN(value.getTime());
  const valueTime = hasValue ? value.getTime() : undefined;

  const formatValue = (date?: Date) =>
    date instanceof Date && !Number.isNaN(date.getTime()) ? dayjs(date).format(dateFormat) : '';

  const [text, setText] = useState(() => formatValue(value));
  const [isTextValid, setIsTextValid] = useState(true);
  // The invalid look waits until the field is left or the date is complete, not the first digit
  const [isTouched, setIsTouched] = useState(false);

  // A new value from outside (calendar, clear button, form reset) replaces the typed text
  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the value time and format only
  useEffect(() => {
    setText(formatValue(value));
    setIsTextValid(true);
    setIsTouched(false);
  }, [valueTime, dateFormat]);

  const updateValidity = (isValid: boolean) => {
    setIsTextValid(isValid);
    onValidityChange?.(isValid);
  };

  const handleTextChange = (nextText: string) => {
    setText(nextText);

    const isEmpty = nextText.trim() === '';
    const matches = (isGerman ? GERMAN_DATE_PATTERN : DEFAULT_DATE_PATTERN).test(nextText);
    updateValidity(isEmpty || matches);

    if (matches) {
      // Strict, so an impossible date like 02.31 is invalid instead of rolling over to March
      const parsed = dayjs(nextText, dateFormat, true);
      if (parsed.isValid()) {
        onChange(parsed.toDate());
      } else {
        updateValidity(false);
      }
    } else if (isEmpty) {
      onChange(undefined);
    }
  };

  const handleSelect = (date?: Date) => {
    setText(formatValue(date));
    updateValidity(true);
    onChange(date ?? undefined);
  };

  const showInvalid = invalid || (!isTextValid && (isTouched || text.length >= dateFormat.length));

  return (
    <Popover>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('openCalendar')}
              className="absolute inset-y-0 left-0 flex w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <Input
            id={id}
            type="text"
            autoComplete="off"
            value={text}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={showInvalid ? true : undefined}
            placeholder={t('date-type')}
            onChange={(event) => handleTextChange(event.target.value)}
            onBlur={() => setIsTouched(true)}
            className={cn('pl-9', showInvalid && 'border-destructive', className)}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={isGerman ? de : enUS}
          selected={hasValue ? value : undefined}
          defaultMonth={hasValue ? value : undefined}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
